<div class="cf__stack">
  <!-- Campaign dates -->
  <app-form-wrapper title="Campaign dates">
    <form [formGroup]="campaignSchedule" (submit)="$event.preventDefault()">
      <div class="cf__body">
        <p class="cf__lead">Messages can only be scheduled between these two dates.</p>

        <div class="cf__row">
          <!-- Start date -->
          <input-field
            label="Starts"
            [errorMessage]="
              campaignStart?.errors?.['minDate'] && campaignStart?.dirty
                ? 'The start date can’t be in the past.'
                : submitted && campaignStart?.errors?.['required']
                  ? 'Choose a start date.'
                  : ''
            "
          >
            <input
              type="date"
              id="cf-start"
              class="input-style"
              formControlName="campaignStart"
              [min]="today"
              (change)="validateStartDate()"
              (change)="scheduleService.campaignSchedule.campaignStart = campaignStart!.value"
            />
          </input-field>

          <!-- End date -->
          <input-field
            label="Ends"
            [errorMessage]="
              campaignEnd?.errors?.['minDate'] && campaignStart?.dirty
                ? 'The end date can’t be before the start date.'
                : submitted && campaignEnd?.errors?.['required']
                  ? 'Choose an end date.'
                  : ''
            "
          >
            <input
              type="date"
              id="cf-end"
              class="input-style"
              formControlName="campaignEnd"
              [min]="campaignStart?.value ? campaignStart?.value : today"
              (change)="validateEndDate()"
              (change)="scheduleService.campaignSchedule.campaignEnd = campaignEnd!.value"
            />
          </input-field>
        </div>
      </div>
    </form>
  </app-form-wrapper>

  <!-- Send times -->
  <app-form-wrapper title="Send times">
    <form
      [formGroup]="messageSchedule"
      (submit)="$event.preventDefault()"
      class="cf__sends"
      [class.is-locked]="!(startDateSet && endDateSet)"
    >
      <div class="cf__body">
        @if (startDateSet && endDateSet) {
          <p class="cf__lead">
            Pick a message, then every date and time it should go out. Each message needs at least one.
          </p>
        } @else {
          <p class="cf__lead"><strong>Set the campaign dates first.</strong> Send times must fall between them.</p>
        }

        <!-- message; the controls below stay out of reach (inert) until the dates are set -->
        <input-field
          [attr.inert]="!(startDateSet && endDateSet) || null"
          label="Message"
          [errorMessage]="messageSchedule.get('message')?.errors?.['required'] && scheduleSubmitted ? 'Choose the message to schedule.' : ''"
        >
          <select id="cf-schedule-message" class="input-style" formControlName="message">
            <option value="" disabled selected>Choose a message</option>
            @for (message of messagesService.savedMessages; track message.id) {
              <option [value]="message.id">
                {{ message.subject || "Untitled message" }}{{ scheduledLabel(message.id) }}
              </option>
            }
          </select>
        </input-field>

        <ng-container formArrayName="messageDates">
          @for (control of messageDates.controls; track control; let i = $index) {
            <div class="cf__item cf__send" [formGroupName]="i" [attr.inert]="!(startDateSet && endDateSet) || null">
              <div class="cf__row cf__row--3">
                <!-- date -->
                <input-field
                  [label]="$count > 1 ? 'Date ' + (i + 1) : 'Date'"
                  [errorMessage]="
                    control.get('date')?.errors?.['outOfRange'] && control.get('date')?.dirty
                      ? 'Pick a date between the campaign start and end.'
                      : control.get('date')?.errors?.['required'] && scheduleSubmitted
                        ? 'Choose a date.'
                        : ''
                  "
                >
                  <input
                    type="date"
                    class="input-style"
                    formControlName="date"
                    [min]="campaignStart?.value"
                    [max]="campaignEnd?.value"
                    (change)="validateMessageDate(i)"
                    (change)="validateMessageTime(i)"
                  />
                </input-field>

                <!-- time -->
                <input-field
                  [label]="$count > 1 ? 'Time ' + (i + 1) : 'Time'"
                  [errorMessage]="
                    control.get('time')?.errors?.['past']
                      ? 'That time has passed. Pick a later one.'
                      : control.get('time')?.errors?.['required'] && scheduleSubmitted
                        ? 'Choose a time.'
                        : ''
                  "
                >
                  <input type="time" class="input-style" formControlName="time" (change)="validateMessageTime(i)" />
                </input-field>

                <!-- time zone -->
                <input-field
                  [label]="$count > 1 ? 'Time zone ' + (i + 1) : 'Time zone'"
                  [errorMessage]="control.get('timezone')?.errors?.['required'] && scheduleSubmitted ? 'Choose a time zone.' : ''"
                >
                  <select class="input-style" formControlName="timezone" (change)="validateMessageTime(i)">
                    <option value="" disabled>Choose a time zone</option>
                    @for (tz of getTimeZones; track $index) {
                      <option [value]="tz.timezone">{{ tz.timezone }}</option>
                    }
                  </select>
                </input-field>
              </div>

              @if ($count > 1) {
                <button
                  type="button"
                  class="bk-icon-btn cf__remove"
                  [attr.aria-label]="'Remove send time ' + (i + 1)"
                  matTooltip="Remove this time"
                  (click)="removeMessageDates(i)"
                >
                  <span class="bk-sym" aria-hidden="true">close</span>
                </button>
              }
            </div>
          }
        </ng-container>

        <div class="cf__add-row cf__add-row--split" [attr.inert]="!(startDateSet && endDateSet) || null">
          <button type="button" class="cf__add" (click)="addMessageDates()">
            <span class="bk-sym" aria-hidden="true">add</span>
            Add another time
          </button>
          <button type="button" class="bk-btn bk-btn--ghost" (click)="saveSchedule()">
            <span class="bk-sym" aria-hidden="true">event_available</span>
            {{ messageDates.length === 1 ? "Add send time" : "Add " + messageDates.length + " send times" }}
          </button>
        </div>
      </div>
    </form>

    <!-- footer -->
    <div class="cf__foot">
      <button type="button" class="bk-btn bk-btn--ghost" (click)="this.cancelCampaignService.openCancelModal()">
        Cancel
      </button>
      <div class="cf__foot-end">
        @if (campaignSchedule.invalid) {
          <p class="cf__foot-note">Set the campaign dates to continue.</p>
        } @else if (unscheduledMessages.length) {
          <p class="cf__foot-note">
            @if (unscheduledMessages.length === 1) {
              “{{ unscheduledMessages[0].subject || "Untitled message" }}” has no send time yet.
            } @else {
              {{ unscheduledMessages.length }} messages have no send time yet.
            }
          </p>
        }
        <button type="button" class="bk-btn bk-btn--ghost" (click)="goBack()">Back</button>
        <button type="button" class="bk-btn bk-btn--primary" [disabled]="blocked" (click)="handleSubmit()">
          Review campaign
          <span class="bk-sym cf__go" aria-hidden="true">arrow_forward</span>
        </button>
      </div>
    </div>
  </app-form-wrapper>
</div>
